import { memo } from 'react';
import { Cpu, Gauge, TrendingUp, Users } from 'lucide-react';
import { cn } from '../../utils/cn';
import { POPULARITY_WINDOW_LABELS } from '../../utils/starchart';
import { POPULARITY_WINDOWS, type PopularityWindow, type StarchartMetric } from '../../types/starchart';

const METRICS: {
  value: StarchartMetric;
  label: string;
  icon: typeof Users;
  hint: string;
}[] = [
  { value: 'players', label: 'Players', icon: Users, hint: 'Players online over time' },
  { value: 'tps', label: 'TPS', icon: Gauge, hint: 'Ticks per second — 20 is healthy' },
  { value: 'mspt', label: 'MSPT', icon: Cpu, hint: 'Milliseconds per tick — lower is better' },
  {
    value: 'popularity',
    label: 'Popularity',
    icon: TrendingUp,
    hint: 'Popularity index and rank over time',
  },
];

interface StarchartMetricTabsProps {
  metric: StarchartMetric;
  onMetricChange: (metric: StarchartMetric) => void;
  popularityWindow: PopularityWindow;
  onPopularityWindowChange: (window: PopularityWindow) => void;
  /** Hide metrics the current view has no data source for. */
  available?: StarchartMetric[];
  className?: string;
}

/** Metric switch, plus the ranking window that only applies to popularity. */
export const StarchartMetricTabs = memo(
  ({
    metric,
    onMetricChange,
    popularityWindow,
    onPopularityWindowChange,
    available,
    className,
  }: StarchartMetricTabsProps) => {
    const metrics = available ? METRICS.filter((m) => available.includes(m.value)) : METRICS;

    return (
      <div
        className={cn(
          // The four metrics plus a window switch overflow a phone; let the strip scroll rather
          // than wrap into a second row that pushes the chart down.
          'flex flex-col sm:flex-row sm:items-center gap-2 max-w-full overflow-x-auto scrollbar-hide',
          className,
        )}
      >
        <div className="segmented shrink-0" role="group" aria-label="Metric">
          {metrics.map((option) => (
            <button
              key={option.value}
              type="button"
              className="segmented-item"
              data-active={metric === option.value}
              onClick={() => onMetricChange(option.value)}
              title={option.hint}
            >
              <option.icon className="w-4 h-4" />
              {option.label}
            </button>
          ))}
        </div>

        {metric === 'popularity' && (
          <div className="segmented shrink-0" role="group" aria-label="Ranking window">
            {POPULARITY_WINDOWS.map((window) => (
              <button
                key={window}
                type="button"
                className="segmented-item segmented-item-sm"
                data-active={popularityWindow === window}
                onClick={() => onPopularityWindowChange(window)}
                title={`${POPULARITY_WINDOW_LABELS[window]} ranking window`}
              >
                {window}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);

StarchartMetricTabs.displayName = 'StarchartMetricTabs';
